import type { DraftSegment, FullResponseDocument, FullResponseSection } from '@/lib/api'

/**
 * Editor segments for a reader who cannot open an AI session.
 *
 * The editor normally gets a section's saved text from `POST /agent/sessions`,
 * which creates a session and is therefore refused to a view-only user (403).
 * The Full response read model (`GET /artifacts/{id}/document`) carries the
 * same saved text and is open to anyone who can see the bid, so a viewer's
 * editor is filled from it instead.
 *
 * The result is display-only: it carries no session, and the editor it feeds
 * is read-only, so nothing here is ever saved back.
 */

/**
 * The section's html with the reader's `<div data-segment-id>` wrappers
 * removed — one part per segment, in order. Falls back to the whole html as
 * a single part when it carries no wrappers (an older backend).
 */
function segmentParts(html: string): string[] {
  if (typeof DOMParser === 'undefined') return [html]
  const body = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html').body
  const wrappers = Array.from(body.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement && el.hasAttribute('data-segment-id'),
  )
  if (wrappers.length === 0) return [html]
  return wrappers.map((el) => el.innerHTML)
}

function pickSection(document: FullResponseDocument, sectionKey: string): FullResponseSection | null {
  const exact = document.sections.find((s) => s.sectionKey === sectionKey)
  if (exact) return exact
  // A single document is one section whatever its key is called.
  return document.sections.length === 1 ? (document.sections[0] ?? null) : null
}

/**
 * The one segment the editor shows for `sectionKey`, or none when nothing has
 * been written there yet.
 *
 * Several segments behind one section (a single document built from per-section
 * rows) are joined into one: citation numbers restart in each segment, so their
 * refs cannot be merged without pointing markers at the wrong source — the
 * joined segment carries refs only when there was exactly one segment.
 */
export function readOnlySegmentsFromDocument(
  document: FullResponseDocument,
  sectionKey: string,
): DraftSegment[] {
  const section = pickSection(document, sectionKey)
  if (!section || section.html.trim().length === 0) return []

  const parts = segmentParts(section.html)
  const refs = section.segments?.length === 1 ? section.segments[0] : undefined
  const updatedAt = section.updatedAt ?? ''

  return [
    {
      id: section.segments?.[0]?.segmentId ?? `read-only:${section.sectionKey}`,
      sessionId: '',
      sectionId: sectionKey,
      heading: section.title,
      content: parts.join('\n'),
      status: 'fresh',
      version: 0,
      wordCount: section.wordCount,
      editedWhileStale: false,
      coverageRefs: [],
      citationRefs: refs?.citationRefs ?? [],
      libraryCitationRefs: refs?.libraryCitationRefs ?? [],
      createdAt: updatedAt,
      updatedAt,
    },
  ]
}
